import { useEffect, useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Bell, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { LOGO_URL } from "@/lib/api";

const PRIMARY = [
  { to: "/", label: "Home" },
  { to: "/divisions", label: "Divisions" },
  { to: "/tournaments", label: "Tournaments" },
  { to: "/promos", label: "News" },
];

const MORE = [
  { to: "/champions", label: "Champions" },
  { to: "/videos", label: "Videos" },
  { to: "/merchandise", label: "Merchandise" },
  { to: "/partners", label: "Partners" },
  { to: "/about", label: "About" },
  { to: "/links", label: "Links" },
  { to: "/contact", label: "Contact" },
];

const NAV = [...PRIMARY, ...MORE];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [live, setLive] = useState(null);
  const [orbitOn, setOrbitOn] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    api.get("/site").then((r) => setOrbitOn(!!r.data?.orbit_published)).catch(() => {});
  }, []);

  const moreLinks = orbitOn ? [...MORE, { to: "/orbit", label: "ORBIT Chalk" }] : MORE;
  const navLinks = [...PRIMARY, ...moreLinks];

  useEffect(() => {
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const url = "/live-status";
    api.get(url).then((r) => setLive(r.data)).catch(() => {});
    const t = setInterval(() => {
      api.get(url).then((r) => setLive(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (live?.is_live && !sessionStorage.getItem("qbca_live_toast")) {
      sessionStorage.setItem("qbca_live_toast", "1");
      toast("🔴 We're live now!", {
        description: live.title || "Watch our league stream on YouTube",
        duration: 9000,
        action: {
          label: "Watch",
          onClick: () => window.open(live.watch_url, "_blank", "noopener,noreferrer"),
        },
      });
    }
  }, [live]);

  const liveBadge = live?.is_live ? (
    <a
      href={live.watch_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="navbar-live-badge"
      title={live.title || "Live now on YouTube"}
      className="inline-flex items-center gap-1.5 bg-[#E11D2A] text-white rounded-full pl-2 pr-3 py-1 hover:bg-[#c41320] transition-colors"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
      </span>
      <span className="text-xs font-bold uppercase tracking-wider">Live</span>
    </a>
  ) : null;

  return (
    <nav className="sticky top-0 z-50 bg-[#0B0B0C] text-white shadow-md" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
            <img src={LOGO_URL} alt="Quinte BCA Pool League" className="h-14 w-14 object-contain drop-shadow-[0_0_12px_rgba(30,99,196,0.4)]" />
            <span className="font-heading font-extrabold text-lg leading-tight tracking-tight">
              Quinte BCA<span className="hidden sm:inline"> Pool League</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-0.5">
            {PRIMARY.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `px-2.5 py-2 rounded-md text-[13px] font-medium transition-colors ${
                    isActive ? "bg-[#1E63C4] text-white" : "text-gray-300 hover:bg-[#1c1c1e] hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}

            <div className="relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                data-testid="nav-more-toggle"
                aria-expanded={moreOpen}
                className={`inline-flex items-center gap-1 px-2.5 py-2 rounded-md text-[13px] font-medium transition-colors ${
                  moreOpen ? "bg-[#1c1c1e] text-white" : "text-gray-300 hover:bg-[#1c1c1e] hover:text-white"
                }`}
              >
                More <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 mt-1 w-48 rounded-lg bg-[#141416] border border-[#26262a] shadow-xl py-1.5 z-50" data-testid="nav-more-menu">
                  {moreLinks.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      data-testid={`nav-${item.label.toLowerCase()}-link`}
                      className={({ isActive }) =>
                        `block px-4 py-2 text-[13px] font-medium transition-colors ${
                          isActive ? "text-[#62A8E5]" : "text-gray-300 hover:bg-[#1E63C4] hover:text-white"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            {liveBadge && <span className="ml-2">{liveBadge}</span>}
            <NavLink
              to="/notify"
              data-testid="nav-notify-link"
              title="Get notified about updates"
              className={({ isActive }) =>
                `ml-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                  isActive ? "bg-[#1E63C4] text-white" : "bg-[#1E63C4]/15 text-[#62A8E5] hover:bg-[#1E63C4] hover:text-white"
                }`
              }
            >
              <Bell className="h-4 w-4" /> Notify Me
            </NavLink>
          </div>

          <div className="flex items-center gap-3 lg:hidden">
            {liveBadge}
            <NavLink to="/notify" data-testid="mobile-nav-notify-icon" aria-label="Get notified" className="p-2 rounded-md hover:bg-[#1c1c1e] text-[#62A8E5]">
              <Bell className="h-5 w-5" />
            </NavLink>
            <button
              className="p-2 rounded-md hover:bg-[#1c1c1e]"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#1f1f22] bg-[#0B0B0C] pb-3" data-testid="mobile-menu">
          <NavLink
            to="/notify"
            data-testid="mobile-nav-notify-link"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-3 text-base font-semibold ${isActive ? "bg-[#1E63C4]" : "text-[#62A8E5] hover:bg-[#1c1c1e]"}`
            }
          >
            <Bell className="h-5 w-5" /> Notify Me
          </NavLink>
          {navLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${item.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `block px-4 py-3 text-base font-medium ${
                  isActive ? "bg-[#1E63C4]" : "hover:bg-[#1c1c1e]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  );
}
